/**
 * Persistence boundary for the user's session-timeout preference.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * This module is a LEAF, and must stay one. `logoutStorage.ts` reads
 * {@link USER_SESSION_TIMEOUT_STORAGE_KEY} from here to keep the key across a
 * logout sweep. Declaring it in `session-timeout-state.ts` instead pulled that
 * module's `useQueryUser` graph into `logoutStorage`, closing the cycle
 * logoutStorage -> session-timeout-state -> useQueryUser -> authActions ->
 * logoutStorage and breaking every suite that touches auth storage (GRO-3989).
 * Do NOT import a query hook, the store, or `@/features/flags` here.
 * ───────────────────────────────────────────────────────────────────────────
 */
import { createUserScopedStorageAtom } from '@/lib/jotai-storage/user-scoped';
import type { EffectiveSessionTimeout } from '@heidi/workspace/contracts';

/**
 * User's last saved session-timeout snapshot — persisted per user so it
 * survives logout and GET /user refetches that still return null minutes.
 * No PHI: only minutes + resolver source.
 */
// UNCARVED DOMAIN KEY (settings, GRO-3989): session-timeout has no package;
// user-scoped localStorage for AutoLogout continuity after opt-in, not a TanStack cache identity.
// eslint-disable-next-line @heidi/rules/no-new-cache-key-declaration -- user-scoped localStorage key for session timeout, not a TanStack cache identity (GRO-3989)
export const USER_SESSION_TIMEOUT_STORAGE_KEY = 'heidi.userSessionTimeout';

export const userSessionTimeoutOverrideAtom =
  createUserScopedStorageAtom<EffectiveSessionTimeout | null>(
    USER_SESSION_TIMEOUT_STORAGE_KEY,
    null
  );
